import { boolean, createBuilder, createSchema, json, number, relationships, string, table } from '@rocicorp/zero';
import type { ColumnBuilder, Schema, SchemaValue, TableBuilderWithColumns, TableSchema } from '@rocicorp/zero';

import { TABLES } from './tables';
import type { ColumnSpec, TableSpec } from './tables';

type AnyColumnBuilder = ColumnBuilder<SchemaValue>;
type AnyTableBuilder = TableBuilderWithColumns<TableSchema>;

function baseColumnBuilder(column: ColumnSpec): AnyColumnBuilder {
  switch (column.kind) {
    case 'text':
      return string() as AnyColumnBuilder;
    case 'bool':
      return boolean() as AnyColumnBuilder;
    case 'json':
      return json() as AnyColumnBuilder;
    default:
      return number() as AnyColumnBuilder;
  }
}

function columnBuilder(column: ColumnSpec): AnyColumnBuilder {
  const named = column.ts === column.pg ? baseColumnBuilder(column) : baseColumnBuilder(column).from(column.pg);
  return (column.nullable ? named.optional() : named) as AnyColumnBuilder;
}

function tableBuilder(spec: TableSpec): AnyTableBuilder {
  const named = spec.name === spec.pg ? table(spec.name) : table(spec.name).from(spec.pg);
  const columns = Object.fromEntries(spec.columns.map((column) => [column.ts, columnBuilder(column)]));
  const [firstKey, ...restKeys] = spec.primaryKey;
  return named.columns(columns).primaryKey(firstKey as string, ...restKeys) as AnyTableBuilder;
}

const builders = new Map(TABLES.map((spec) => [spec.name, tableBuilder(spec)]));

function builderOf(name: string): AnyTableBuilder {
  const builder = builders.get(name);
  if (builder === undefined) {
    throw new Error(`Нет таблицы «${name}» в реестре`);
  }
  return builder;
}

function channelScopedRelationships() {
  const channel = builderOf('channel');
  const members = relationships(channel, ({ many }) => ({
    members: many({ sourceField: ['id'], destField: ['channelId'], destSchema: builderOf('channelMember') }),
  }));
  const scoped = TABLES.filter((spec) => spec.visibility === 'channel' && spec.name !== 'channel');
  const owningChannel = scoped.map((spec) =>
    relationships(builderOf(spec.name), ({ one }) => ({
      channel: one({
        sourceField: [spec.channelColumn as string],
        destField: ['id'],
        destSchema: channel,
      }),
    })),
  );
  return [members, ...owningChannel];
}

export const schema: Schema = createSchema({
  tables: [...builders.values()],
  relationships: channelScopedRelationships(),
});

export const zql = createBuilder(schema);
